import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormCustomersComponent } from '../components/customers-form/customers-form.component';
import { FormContactsCustomersComponent } from '../components/contacts-customers-form/contacts-customers-form.component';
import { FormNotesCustomersComponent } from '../components/notes-customers-form/notes-customers-form.component';
import { RankingBarComponent } from '../components/ranking-bar/ranking-bar.component';
import { CustomerService } from '../../../services/api/customer.service';
import { ActivatedRoute } from '@angular/router';

import { AttachmentsPanelComponent } from '../components/attachments-panel/attachments-panel.component';
import { AlertNotificationService } from '../../../services/alert-notification.service';
import { MainLayoutService } from '../../../services/main-layout.service';
import { CustomersClientCodeComponent } from '../components/customers-client-code/customers-client-code.component';
import { HttpParams } from '@angular/common/http';
import { OtherDocumentsComponent } from '../components/other-documents/other-documents.component';
import { RouteService } from '../../../services/route.service';

@Component({
  selector: 'app-customers-edit-page',
  imports: [FormCustomersComponent, FormContactsCustomersComponent, FormNotesCustomersComponent, RankingBarComponent, AttachmentsPanelComponent, CustomersClientCodeComponent, OtherDocumentsComponent],
  templateUrl: './customers-edit-page.component.html',
  styleUrl: './customers-edit-page.component.scss'
})
export class CustomersEditPageComponent implements OnInit, OnDestroy {
  customer: any;
  customerNotes: any;
  clientPersons: any;
  readonly: boolean;
  clientsId!: number;
  isCreating: boolean = false;
  rankingValue: number = 0;
  code: string = '';
  codeHasError: boolean = false;

  @ViewChild('customersForm') customersForm!: FormCustomersComponent;
  @ViewChild('contactsForm') contactsForm!: FormContactsCustomersComponent;
  @ViewChild('notesForm') notesForm!: FormNotesCustomersComponent;

  constructor(
    protected customerService: CustomerService,
    private route: ActivatedRoute,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private routeService: RouteService
  ) {
    this.readonly = false;
  }

  ngOnInit() {
    const clientsIdParam = this.route.snapshot.paramMap.get('clientsId');
    const params = new HttpParams().set('size', '9999');
    this.clientsId = Number(clientsIdParam);
    this.isCreating = !clientsIdParam;

    if (clientsIdParam) {
      this.customerService.detail(this.clientsId).subscribe(response => {
        if (response.status.code === 200) {
          this.customer = response.data.customer;
          this.rankingValue = this.customer.ranking || 0;
          this.code = this.customer.code || '';
          this.mainLayoutService.setRelatedClientName(this.customer?.companyName || null);
        }
      });

      this.customerService.listClientPersons(this.clientsId).subscribe(response => {
        if (response.status.code === 200) {
          this.clientPersons = response.data.customerPersons;
        }
      });

      this.customerService.listNotes(this.clientsId, { params }).subscribe(response => {
        if (response.status.code === 200) {
          this.customerNotes = response?.data?.sectionNotes;
        }
      });
    }
  }

  onSaveRequested(): void {
    if (this.isCreating) {
      if (!this.contactsForm.hasAnyContact()) {
        this.alertNotificationService.error('Please add at least one contact before saving the client');
        return;
      }
      if (!this.contactsForm.validateContactsForCreate()) {
        this.alertNotificationService.error('Please fix the errors in the Contacts section before saving');
        return;
      }
    }
    this.customersForm.submitForm();
  }

  onCustomerSaved(savedData: any): void {
    if (!this.isCreating || !savedData.success) {
      return;
    }

    const customerId = this.extractCustomerId(savedData.response);
    if (!customerId) {
      return;
    }

    this.contactsForm.saveContactsForNewClient(customerId).subscribe({
      next: () => {
        this.alertNotificationService.success('Client and contacts saved successfully');
        setTimeout(() => {
          this.routeService.customerUpdate(customerId);
        }, 1000);
      },
      error: (error: any) => {
        const msg = error?.error?.error?.message || 'Client saved but contacts could not be saved';
        this.alertNotificationService.error(msg);
      }
    });
  }

  private extractCustomerId(response: any): number | null {
    const rawId = response?.data?.customer?.customerId
      ?? response?.data?.customerId
      ?? response?.data?.id
      ?? response?.customer?.customerId
      ?? response?.customerId
      ?? response?.id;
    const id = Number(rawId);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  updateRanking(newRanking: number): void {
    this.rankingValue = newRanking;
  }

  updateClientCode(newCode: string): void {
    this.code = newCode;
    if (newCode && newCode.trim()) {
      this.codeHasError = false;
    }
  }

  onCodeRequired(isRequired: boolean): void {
    this.codeHasError = isRequired;
  }

  ngOnDestroy(): void {
    this.mainLayoutService.setRelatedClientName(null);
  }
}
